import React, { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronDown, Star } from 'lucide-react';
import ActivityCard from '../components/ActivityCard';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { activities, categories, destinations } from '../mock';

const sortOptions = [
  { v: 'recommended', label: 'Recommended' },
  { v: 'popular', label: 'Most popular' },
  { v: 'price_asc', label: 'Price: Low to High' },
  { v: 'price_desc', label: 'Price: High to Low' },
  { v: 'rating', label: 'Highest rated' },
];

export default function Listing() {
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').toLowerCase();
  const cat = params.get('category') || '';
  const cityParam = params.get('city') || '';

  const [selectedCats, setSelectedCats] = useState(cat ? [cat] : []);
  const [selectedCities, setSelectedCities] = useState(cityParam ? [cityParam] : []);
  const [price, setPrice] = useState([0, 200]);
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState('recommended');

  const cityNameMap = useMemo(() => Object.fromEntries(destinations.map((d) => [d.id, d.name])), []);

  const results = useMemo(() => {
    let list = activities.filter((a) => {
      if (q && !(a.title.toLowerCase().includes(q) || a.city.toLowerCase().includes(q))) return false;
      if (selectedCats.length && !selectedCats.includes(a.category)) return false;
      if (selectedCities.length) {
        const match = selectedCities.some((cid) => cityNameMap[cid] && a.city.toLowerCase() === cityNameMap[cid].toLowerCase());
        if (!match) return false;
      }
      if (a.price < price[0] || a.price > price[1]) return false;
      if (a.rating < minRating) return false;
      return true;
    });
    if (sort === 'price_asc') list = [...list].sort((x, y) => x.price - y.price);
    if (sort === 'price_desc') list = [...list].sort((x, y) => y.price - x.price);
    if (sort === 'rating') list = [...list].sort((x, y) => y.rating - x.rating);
    if (sort === 'popular') list = [...list].sort((x, y) => y.reviews - x.reviews);
    return list;
  }, [q, selectedCats, selectedCities, price, minRating, sort, cityNameMap]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const FiltersPanel = () => (
    <div className="space-y-6">
      <div>
        <div className="font-semibold text-sm mb-2">Category</div>
        <div className="space-y-2">
          {categories.slice(0, 8).map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={selectedCats.includes(c.id)} onCheckedChange={() => toggle(selectedCats, setSelectedCats, c.id)} />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">Destination</div>
        <div className="space-y-2 max-h-56 overflow-auto pr-1">
          {destinations.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={selectedCities.includes(d.id)} onCheckedChange={() => toggle(selectedCities, setSelectedCities, d.id)} />
              <span>{d.name}, {d.country}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">Price range</div>
        <div className="px-1">
          <Slider value={price} max={200} step={5} onValueChange={setPrice} />
          <div className="flex justify-between text-xs text-gray-600 mt-2">
            <span>${price[0]}</span>
            <span>${price[1]}</span>
          </div>
        </div>
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">Minimum rating</div>
        <div className="flex gap-2">
          {[0, 3, 4, 4.5].map((r) => (
            <button key={r} onClick={() => setMinRating(r)} className={`px-3 py-1.5 text-xs rounded-full border ${minRating === r ? 'border-xlook-orange text-xlook-orange bg-orange-50' : 'border-gray-200 text-gray-700'}`}>
              {r === 0 ? 'Any' : `${r}+`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const activeFiltersCount = selectedCats.length + selectedCities.length + (minRating > 0 ? 1 : 0) + ((price[0] !== 0 || price[1] !== 200) ? 1 : 0);

  return (
    <div className="container-k py-4 md:py-8">
      {/* Breadcrumb */}
      <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
        <Link to="/" className="hover:text-gray-700">Home</Link> / <span className="text-gray-700">Search</span>
      </div>
      <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-1">
        {q ? `Results for "${q}"` : cat ? categories.find((c)=>c.id===cat)?.name : cityParam ? destinations.find((d)=>d.id===cityParam)?.name : 'All activities'}
      </h1>
      <div className="text-sm text-gray-600 mb-4">{results.length} activities found</div>

      {/* Mobile filter + sort bar */}
      <div className="md:hidden flex items-center gap-2 mb-3 sticky top-14 bg-white z-20 py-2 -mx-4 px-4 border-b border-gray-100">
        <Sheet>
          <SheetTrigger asChild>
            <button className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 border border-gray-200 rounded-full">
              <SlidersHorizontal className="w-4 h-4" /> Filters {activeFiltersCount > 0 && <span className="bg-xlook-orange text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{activeFiltersCount}</span>}
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl p-0">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-semibold">Filters</div>
              <button onClick={() => { setSelectedCats([]); setSelectedCities([]); setPrice([0, 200]); setMinRating(0); }} className="text-sm text-xlook-orange">Reset</button>
            </div>
            <div className="p-4 overflow-y-auto"><FiltersPanel /></div>
          </SheetContent>
        </Sheet>

        <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm px-3 py-2 border border-gray-200 rounded-full bg-white">
          {sortOptions.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* Desktop filters */}
        <aside className="hidden md:block sticky top-28 self-start">
          <div className="p-4 border border-gray-100 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Filters</div>
              <button onClick={() => { setSelectedCats([]); setSelectedCities([]); setPrice([0, 200]); setMinRating(0); }} className="text-sm text-xlook-orange">Reset</button>
            </div>
            <FiltersPanel />
          </div>
        </aside>

        <div>
          <div className="hidden md:flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600">Showing {results.length} activities</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white">
                {sortOptions.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <div className="text-4xl mb-3">:(</div>
              <p>No activities match your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {results.map((a) => <ActivityCard key={a.id} activity={a} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
